import { apiClient } from '../lib/apiClient';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  roleId: string;
  roleName?: string;
  positionId: string;
  positionName?: string;
  departmentId: string;
  departmentName?: string;
  employeeId: string;
  isActive: boolean;
  joinDate: string;
  status: string;
  totalVacDays: number;
  remainingVacDays: number;
  vacationAccruedMonths?: number;
  vacationLastAccrualDate?: string | null;
  phone?: string | null;
  emergencyContact?: string | null;
  address?: string | null;
  lastLogin?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PermissionData {
  role: string;
  level: number;
  permissions: string[];
  canAccessAll: boolean;
}

export interface SigninResponse {
  token: string;
  user: AuthUser;
  permissions?: PermissionData;
}

export async function login(email: string, password: string): Promise<SigninResponse> {
  const { data } = await apiClient.post('/api/auth/signin', { email, password });
  if (!data?.ok) throw new Error(data?.error?.message || 'Login failed');
  return data.data as SigninResponse;
}

export async function register(params: { email: string; password: string; name: string; roleId?: string; positionId?: string; departmentId?: string; }): Promise<SigninResponse> {
  const { data } = await apiClient.post('/api/auth/signup', params);
  if (!data?.ok) throw new Error(data?.error?.message || 'Registration failed');
  return data.data as SigninResponse;
}

export async function logout(): Promise<void> {
  try {
    await apiClient.post('/api/auth/signout');
  } catch {}
}

export async function getMe(): Promise<AuthUser | null> {
  try {
    const { data } = await apiClient.get('/api/auth/me');
    if (!data?.ok) return null;
    return data.data.user as AuthUser;
  } catch (_e) {
    // Network/CORS/401 errors should not crash app on silent refresh
    return null;
  }
}

export async function updateMe(updates: Partial<AuthUser>): Promise<AuthUser> {
  const { data } = await apiClient.patch('/api/auth/me', updates);
  if (!data?.ok) throw new Error(data?.error?.message || 'Profile update failed');
  return data.data.user as AuthUser;
}
